/**
 * Recursively rounds all numeric values in an object or array.
 * - If the number has leading zeros after the decimal point (e.g., 0.000003333),
 *   it rounds to 3 significant figures.
 * - Otherwise, it rounds to 3 decimal places.
 * - Also handles strings that are purely numeric values.
 * @param {any} obj - The object, array, or value to process
 * @param {number} decimalPlaces - Number of decimal places to round to (default: 3)
 * @returns {any} - The processed value with rounded numbers
 */
export const roundNumericValues = (obj, decimalPlaces = 3) => {
    if (obj === null || obj === undefined) return obj;

    if (Array.isArray(obj)) {
        return obj.map((item) => roundNumericValues(item, decimalPlaces));
    }

    if (typeof obj === "object") {
        const rounded = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const value = obj[key];
                if (typeof value === "number" && !Number.isInteger(value)) {
                    rounded[key] = roundNumber(value, decimalPlaces);
                } else if (typeof value === "string") {
                    rounded[key] = roundNumericString(value, decimalPlaces);
                } else if (typeof value === "object") {
                    rounded[key] = roundNumericValues(value, decimalPlaces);
                } else {
                    rounded[key] = value;
                }
            }
        }
        return rounded;
    }

    return obj;
};

const roundNumber = (value, decimalPlaces) => {
    if (value === 0) return 0;

    const absValue = Math.abs(value);

    // Check if the number is less than 0.001
    if (absValue > 0 && absValue < Math.pow(10, -decimalPlaces)) {
        // Use significant figures
        return parseFloat(value.toPrecision(decimalPlaces));
    }

    // Use decimal places
    return Math.round(value * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
};

const roundNumericString = (value, decimalPlaces) => {
    if (!value || value.trim() === "") return value;

    const trimmedValue = value.trim();
    const parsedNumber = Number(trimmedValue);

    // Only round if the entire string is a valid number
    if (!isNaN(parsedNumber) && isFinite(parsedNumber) && trimmedValue !== "") {
        if (Number.isInteger(parsedNumber)) {
            return value; // Keep integers unchanged
        }
        return String(roundNumber(parsedNumber, decimalPlaces));
    }

    // Return original string if not purely numeric
    return value;
};

export default roundNumericValues;